import React, { useEffect, useRef, useState, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import { getKiinteistoWhole } from "../../api/api";
import KiinteistoDetails from "../KiinteistoDetails";
import { useParams } from "react-router-dom";
import KorttiIdGetter from "../KorttiIdGetter";
import { useRakennusData } from "../../context/RakennusDataContext";
import { useReportTemplateContext } from "./ReportTemplate/ReportTemplateContext";

export default function PropertyInfoAccordion({
  data,
  onChange,
  availableKiinteistot = [],
}) {
  const { id: propertyId } = useParams();

  // ✅ Context access
  const { rakennusData, setRakennusData } = useRakennusData();
  const [localCard, setLocalCard] = useState(rakennusData);
  const prevRakennusData = useRef(rakennusData);

  useEffect(() => {
    if (rakennusData !== prevRakennusData.current) {
      prevRakennusData.current = rakennusData;
      setLocalCard(rakennusData);
    }
  }, [rakennusData]);

  const handleSetCard = (updated) => {
    setLocalCard(updated);
    setRakennusData(updated);
  };
  const { mergedRakennusData } = useReportTemplateContext();

  // Local state
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal
  const [showKorttiModal, setShowKorttiModal] = useState(false);

  /* =========================
     SAFE PROPERTY IDS
     ========================= */

  const extraPropertyIds = useMemo(() => {
    return (data?.propertyIds || []).filter(
      (id) => Number(id) !== Number(propertyId)
    );
  }, [data?.propertyIds, propertyId]);

  const allPropertyIds = useMemo(() => {
    return [propertyId, ...extraPropertyIds].filter(Boolean);
  }, [propertyId, extraPropertyIds]);

  /* =========================
     FETCH ALL PROPERTIES
     ========================= */

  useEffect(() => {
    if (!allPropertyIds.length) return;

    const fetchAll = async () => {
      setLoading(true);
      setError(null);

      try {
        const results = await Promise.all(
          allPropertyIds.map((id) =>
            getKiinteistoWhole(id).then((res) => res.data)
          )
        );

        // ✅ Ensure main property always first
        results.sort((a, b) =>
          Number(a.id_kiinteisto) === Number(propertyId)
            ? -1
            : Number(b.id_kiinteisto) === Number(propertyId)
            ? 1
            : 0
        );

        setProperties(results);
      } catch {
        setError("Tiedon hakeminen epäonnistui.");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [allPropertyIds, propertyId]);

  /* =========================
     ADD / REMOVE PROPERTIES
     ========================= */

  const addProperty = (id) => {
    if (!id) return;
    if (extraPropertyIds.includes(id)) return;

    onChange({
      ...data,
      propertyIds: [...extraPropertyIds, id],
    });
  };

  const removeProperty = (id) => {
    onChange({
      ...data,
      propertyIds: extraPropertyIds.filter((x) => x !== id),
    });
  };

  const mergedCard = useMemo(() => {
    // console.log('Merged rakennusdata at PropertyInfoAccordion:', mergedRakennusData);
    return mergedRakennusData;
  }, [mergedRakennusData]);

  /* =========================
     STORE MERGED INTO CONTEXT
     ========================= */

  // useEffect(() => {
  //   if (mergedCard && extraPropertyIds.length > 0) {
  //     setMergedRakennusData(mergedCard);
  //   } else {
  //     setMergedRakennusData(null); // reset if no merge active
  //   }
  // }, [mergedCard, extraPropertyIds.length, setMergedRakennusData]);

  /* =========================
     RENDER
     ========================= */

  return (
    <div>
      {/* Add property button */}
      <button
        className="btn btn-sm btn-outline-primary mb-3"
        onClick={() => setShowKorttiModal(true)}
      >
        + Lisää kiinteistö
      </button>

      {/* Modal selector */}
      <KorttiIdGetter
        show={showKorttiModal}
        onClose={() => setShowKorttiModal(false)}
        onSelect={(targetId) => {
          addProperty(targetId);
          setShowKorttiModal(false);
        }}
      />

      {/* Added properties list */}
      {extraPropertyIds.length > 0 && (
        <div className="mb-3">
          <strong>Lisätyt kiinteistöt:</strong>

          <ul className="list-unstyled mt-2">
            {extraPropertyIds.map((id) => {
              const p = properties.find(
                (x) => Number(x.id_kiinteisto) === Number(id)
              );

              // Still loading that property
              if (!p) {
                return (
                  <li
                    key={id}
                    className="border rounded px-2 py-1 mb-1 text-muted"
                  >
                    Ladataan kiinteistön tiedot…
                  </li>
                );
              }

              // Extract address info
              const address =
                p.rakennukset_fulls?.[0]?.osoite || "Osoite puuttuu";

              const cityLine = [
                p.rakennukset_fulls?.[0]?.postinumero,
                p.rakennukset_fulls?.[0]?.toimipaikka,
              ]
                .filter(Boolean)
                .join(" ");

              const tunnus = p.kiinteistotunnus || "Kiinteistötunnus puuttuu";

              return (
                <li
                  key={id}
                  className="d-flex justify-content-between align-items-center border rounded px-2 py-2 mb-2"
                >
                  <span>
                    {/* Address */}
                    <div className="fw-semibold">{address}</div>

                    {/* Postal + City */}
                    {cityLine && (
                      <div className="text-muted small">{cityLine}</div>
                    )}

                    {/* Property tunnus */}
                    <div className="text-muted small">{tunnus}</div>
                  </span>

                  {/* Actions */}
                  <div className="d-flex flex-column gap-1 flex-shrink-0">
                  <a
                    href={`/taloyhtiokortti/${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-success"
                  >
                    Avaa
                  </a>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeProperty(id)}
                  >
                    Poista
                  </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}


      {/* Loading */}
      {loading && <Spinner animation="border" />}

      {/* Error */}
      {error && <div className="text-danger">{error}</div>}

      {/* Merged property highlighted */}
      {!loading && mergedCard ? (
        <div
          className="p-2 border rounded mb-3"
          style={{ backgroundColor: "#fff4e6", borderColor: "#ff9900" }}
        >
          <h6 className="fw-bold text-warning mb-2">🔀 Yhdistetty rakennusdata</h6>
          <KiinteistoDetails card={mergedRakennusData} editable={false} hideSharePoint />
        </div>
      ) : (<></>)}
      {/* Original property */}
      {!loading && rakennusData && (
        <div className="p-2 border rounded mb-3">
          <h6 className="fw-bold mb-2">🏠 Kiinteistö</h6>
          <KiinteistoDetails card={localCard} setCard={handleSetCard} hideSharePoint />
        </div>
      )}


    </div>
  );
}
